import { useState } from 'react';
import { BordOneToneIcon } from 'bord-design-system';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import BordUploaderButton from 'components/NudosComponents/BordUploaderButton/BordUploaderButton';
import { ACCEPTED_FILE_EXTENSION } from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import NudosStaticAlertInComponent from 'components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import { IQuoteBulkUpload } from 'types/quote.types';
import { uploadProcurementQuote } from 'services/bulkUpload/uploadQuotes.service';
import { isFileValid } from 'utils/validateFiles';
import { formatShortDate, formatDateToPicker, getTodayDateForPicker } from 'utils/formatDatesAndHours';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './QuoteDetailProcurementUploadCard.scss';

interface IQuoteDetailProcurementUploadCard {
  quoteId: number;
  bulkUpload?: IQuoteBulkUpload | null;
  neededToDate?: string | null;
  onUploadSuccess: () => void;
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MAX_FILE_SIZE_MB = 10;
const FORMAT_ERROR = 'El archivo debe estar en formato .xlsx';
const SIZE_ERROR = `El archivo no debe superar los ${MAX_FILE_SIZE_MB}MB`;

/**
 * Card de la columna derecha en el detalle de cotización exploratoria.
 * Tiene dos modos disjuntos:
 * - Upload: si procurement aún no ha cargado, muestra selector de fecha de vencimiento + dropzone + botón "Subir".
 * - Visualización: si ya cargó, muestra el archivo con botón de descarga y la fecha de vencimiento.
 */
const QuoteDetailProcurementUploadCard = ({
  quoteId,
  bulkUpload,
  neededToDate,
  onUploadSuccess
}: IQuoteDetailProcurementUploadCard) => {
  const [file, setFile] = useState<File | undefined>();
  const [expirationDate, setExpirationDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [errorFileUrl, setErrorFileUrl] = useState<string | undefined>();
  const [dateError, setDateError] = useState('');

  const minExpirationDate = getTodayDateForPicker();
  const isExpirationFuture = !!expirationDate && !dateError;
  const isFormValid = !!file && isExpirationFuture && !isSubmitting;

  /** Valida el archivo seleccionado (formato xlsx, peso < 10MB) antes de fijarlo en el estado. */
  const handleFileSelected = (selectedFile?: File) => {
    if (!selectedFile) {
      setFile(undefined);
      return;
    }
    if (!isFileValid(selectedFile, [XLSX_MIME], MAX_FILE_SIZE_MB, FORMAT_ERROR, SIZE_ERROR)) return;
    setFile(selectedFile);
    setErrors([]);
    setErrorFileUrl(undefined);
  };

  /** Actualiza la fecha de vencimiento y valida que no sea anterior a hoy. */
  const handleDateChange = (value: string) => {
    setExpirationDate(value);
    const selectedDate = formatDateToPicker(value);
    const isPastDate = !!selectedDate && selectedDate < minExpirationDate;
    setDateError(isPastDate ? 'La fecha de vencimiento no puede ser anterior a hoy' : '');
  };

  /** Envía el archivo y la fecha al backend. En éxito refresca el detalle. */
  const handleSubmit = async () => {
    if (!file || !isExpirationFuture) return;
    setIsSubmitting(true);
    setErrors([]);
    setErrorFileUrl(undefined);

    try {
      const response = await uploadProcurementQuote(quoteId, file, expirationDate);
      if (response?.errors?.length) {
        setErrors(response.errors);
        setErrorFileUrl(response.errorFileUrl);
        return;
      }
      onUploadSuccess();
    } catch (error) {
      const message =
        error instanceof SyntaxError
          ? error.message
          : 'Error al subir la cotización, por favor inténtelo de nuevo';
      displayErrorNotification({ customJSXMessage: <>{message}</>, duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeader = () => (
    <header className="cardHeader">
      <span className="stepNumber">2</span>
      <h3 className="cardTitle">Cotización con precios de Procurement</h3>
    </header>
  );

  if (bulkUpload) {
    const fileName = bulkUpload.fileUrl?.split('/')?.pop()?.split('?')?.[0] || 'planilla.xlsx';
    const handleDownload = () => window.open(bulkUpload.fileUrl, '_blank');
    return (
      <section className="quoteDetailProcurementUploadCard filled">
        {renderHeader()}

        {neededToDate && (
          <div className="expirationInfo">
            <span className="expirationLabel">Fecha de vencimiento</span>
            <span className="expirationValue">{formatShortDate(neededToDate)?.onlyDate}</span>
          </div>
        )}

        <button type="button" className="fileRow" onClick={handleDownload}>
          <BordOneToneIcon variant="download" customWidth="1.6rem" />
          <span className="fileName">{fileName}</span>
        </button>
      </section>
    );
  }

  return (
    <section className="quoteDetailProcurementUploadCard">
      {renderHeader()}

      <div className={`datePickerWithPlaceholder${expirationDate ? '' : ' isEmpty'}`}>
        <NudosDatePicker
          label="Fecha de vencimiento"
          updateCurrentValueCallback={handleDateChange}
          isFilled={!!expirationDate}
          errorText={dateError}
          customClassName="expirationDatePicker"
          minDate={minExpirationDate}
        />
      </div>

      <div className="procurementUploader">
        <BordUploaderButton
          title={
            <p className="mb-0">
              <span className="uploadLink">Busca</span> o arrastra la cotización
            </p>
          }
          emptyBordUploaderButtonSubtitle={`Formatos aceptados: .xlsx · Peso máximo: ${MAX_FILE_SIZE_MB} MB`}
          acceptFileExtension={[ACCEPTED_FILE_EXTENSION.XLSX]}
          isFilled={!!file}
          defaultFile={file ? { documentName: file.name, name: file.name } : undefined}
          handleChangeFile={handleFileSelected}
          handleDeleteFile={() => handleFileSelected(undefined)}
          modeReplaceFile
          customContainerClassName="procurementUploaderContainer"
        />
      </div>

      {!!errors.length && (
        <NudosStaticAlertInComponent
          color="red"
          type="warning"
          alertOptions={{ title: 'La cotización contiene errores' }}
          bodyAlertComponent={
            <div className="errorsList">
              {errors.map(err => (
                <p key={err} className="errorItem">
                  {err}
                </p>
              ))}
              {errorFileUrl && (
                <a className="errorFileLink" href={errorFileUrl} target="_blank" rel="noreferrer">
                  Descargar archivo con errores
                </a>
              )}
            </div>
          }
        />
      )}

      <div className="submitRow">
        <NudosButton
          buttonText="Subir cotización"
          handleClick={handleSubmit}
          isButtonDisabled={!isFormValid}
          isButtonLoading={isSubmitting}
          componentSize="medium"
          colorPalette="primary"
        />
      </div>
    </section>
  );
};

export default QuoteDetailProcurementUploadCard;
